import {
  Button,
  Col,
  DatePicker,
  Form,
  Layout,
  message,
  Modal,
  Row,
  Tabs,
  Table,
  Dropdown,
  Menu,
  Upload,
} from "antd";
import {
  ChevronDown,
  Eye,
  FileText,
  Inbox,
  Pen,
  Printer,
  Upload as UploadIcon,
} from "lucide-react";
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import ViewAttachmentModal from "../../Components/CustomModals/ViewAttachmentModal";
import {
  formatDate,
  formatNumbers,
  formatString,
  getDateRange,
  navigateTo,
  getUserRole,
} from "../../Utils/Common";
import { hasRole } from "../../Utils/AccessControl";
import { getRequest, postRequest } from "../../Utils/ApiCalls";
import dayjs from "dayjs";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import { useTableData } from "../../Utils/useTableData";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Logo from "../../Assets/Images/logo.png";

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;
const { Dragger } = Upload;

function getDepositSlipUrls(record: any): string[] {
  const depositSlip = record?.deposit_slip;

  if (Array.isArray(depositSlip) && depositSlip.length > 0) {
    return depositSlip
      .map((item: any) =>
        typeof item === "string" ? item : item?.url || item?.attachment_url || ""
      )
      .filter(Boolean);
  }

  if (typeof depositSlip === "string" && depositSlip) {
    return [depositSlip];
  }

  return [];
}

function formatSourceType(value: any) {
  if (value === "petty_cash_liquidation") return "Petty Cash Liquidation";
  if (value === "purchase_order") return "Purchase Order";
  return formatString(value) || "-";
}

const PaymentRequest = () => {
  const [loading, setLoading] = useState("");
  const [showModal, setShowModal] = useState("");
  const dateRange = getDateRange("month");
  const [form] = Form.useForm();
  const [depositForm] = Form.useForm();
  const [selectedRow, setSelectedRow]: any = useState({});
  const [activeTab, setActiveTab] = useState("pending");
  const [showAttachment, setShowAttachment] = useState<string | null>(null);
  const [voucherDetail, setVoucherDetail] = useState<any>(null);
  const [voucherGenerated, setVoucherGenerated] = useState(false);

  const {
    dataSource,
    dataSize,
    loading: tableLoading,
    filterConfig,
    setFilterConfig,
    refetch: fetchTableData,
    handleChangePage,
    handlePageSizeChange,
  } = useTableData<any>({
    endpoint: "payment_requests/get",
    initialFilters: {
      date_from: formatDate(dateRange[0], "YYYY-MM-DD"),
      date_to: formatDate(dateRange[1], "YYYY-MM-DD"),
      status: "pending",
    },
    isFormData: true,
  });

  function clearFields() {
    setSelectedRow({});
    form.resetFields();
    depositForm.resetFields();
    setVoucherDetail(null);
    setVoucherGenerated(false);
  }

  function onChangeTab(activeKey: any) {
    setActiveTab(activeKey);
    setFilterConfig((prev: any) => ({
      ...prev,
      status: activeKey,
      page: 1,
    }));
  }

  async function openCheckVoucherModal(record: any) {
    setSelectedRow(record);
    setVoucherGenerated(false);
    setShowModal("voucher");
    setLoading("voucher");

    const response = await getRequest(
      "payment_requests/get",
      { payment_request_id: record.id },
      true
    );

    if (response.success) {
      setVoucherDetail(response.data.data?.[0] ?? record);
    } else {
      setVoucherDetail(record);
      message.warning("Could not load full payment request details.");
    }
    setLoading("");
  }

  function openDepositSlipModal(record: any) {
    setSelectedRow(record);
    depositForm.resetFields();
    setShowModal("deposit");
  }

  function closeActionModal() {
    setShowModal("");
    setVoucherDetail(null);
    setVoucherGenerated(false);
    depositForm.resetFields();
  }

  async function loadLogoDataUrl(): Promise<string | null> {
    try {
      const response = await fetch(Logo);
      const blob = await response.blob();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  }

  async function buildCheckVoucherPdf() {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const marginX = 15;
    const contentW = pageW - marginX * 2;
    const detail = voucherDetail || selectedRow || {};
    const orders = Array.isArray(detail.orders) ? detail.orders : [];
    const total =
      Number(detail.grand_total) ||
      orders.reduce(
        (sum: number, item: any) => sum + (Number(item?.grand_total) || 0),
        0
      );
    const seriesNo = detail.id ?? selectedRow?.id ?? "";
    const payee =
      detail.supplier_name ||
      orders.map((o: any) => o.supplier_name).filter(Boolean)[0] ||
      "";
    const address =
      detail.supplier_address ||
      detail.address ||
      orders.map((o: any) => o.supplier_address || o.address).filter(Boolean)[0] ||
      "";
    const voucherDate = dayjs(
      detail.approved_on || detail.transaction_date || undefined
    ).format("MMMM D, YYYY");
    const preparedBy =
      detail.added_by_name || detail.created_by_name || "";
    const approvedBy = detail.approved_by_name || "";
    const checkedBy = detail.checked_by_name || "";

    // ── Header ──
    const logoDataUrl = await loadLogoDataUrl();
    if (logoDataUrl) {
      try {
        doc.addImage(logoDataUrl, "PNG", marginX, 10, 22, 22);
      } catch {
        // skip logo if image type fails
      }
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("CEBU AGUA LAB, INC.", pageW / 2, 16, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(
      "ADG IT Center 6th Floor, G. Lopez Jaena St., Subangdaku, Mandaue City",
      pageW / 2,
      21,
      { align: "center" }
    );
    doc.text("Email Add: Cebuagualabinc@gmail.com", pageW / 2, 25, {
      align: "center",
    });
    doc.text("Tel No.: (032) 342-0436", pageW / 2, 29, { align: "center" });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Series No", pageW - marginX - 28, 14);
    doc.setTextColor(200, 0, 0);
    doc.setFontSize(16);
    doc.text(String(seriesNo), pageW - marginX - 14, 22, { align: "center" });
    doc.setTextColor(0, 0, 0);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("CHECK VOUCHER FORM", pageW / 2, 38, { align: "center" });

    // ── Payee / Address / Date / Check No ──
    const fieldY1 = 48;
    const fieldY2 = 56;
    const leftLabelX = marginX;
    const leftLineX = marginX + 22;
    const leftLineW = 95;
    const rightLabelX = marginX + 125;
    const rightLineX = marginX + 145;
    const rightLineW = contentW - 145;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("PAYEE:", leftLabelX, fieldY1);
    doc.setFont("helvetica", "normal");
    doc.line(leftLineX, fieldY1 + 1, leftLineX + leftLineW, fieldY1 + 1);
    doc.text(String(payee), leftLineX + 1, fieldY1 - 1);

    doc.setFont("helvetica", "bold");
    doc.text("Date:", rightLabelX, fieldY1);
    doc.setFont("helvetica", "normal");
    doc.line(rightLineX, fieldY1 + 1, rightLineX + rightLineW, fieldY1 + 1);
    doc.text(voucherDate, rightLineX + 1, fieldY1 - 1);

    doc.setFont("helvetica", "bold");
    doc.text("ADDRESS:", leftLabelX, fieldY2);
    doc.setFont("helvetica", "normal");
    doc.line(leftLineX, fieldY2 + 1, leftLineX + leftLineW, fieldY2 + 1);
    if (address) {
      doc.setFontSize(8);
      doc.text(String(address), leftLineX + 1, fieldY2 - 1, {
        maxWidth: leftLineW - 2,
      });
      doc.setFontSize(10);
    }

    doc.setFont("helvetica", "bold");
    doc.text("Check No:", rightLabelX, fieldY2);
    doc.setFont("helvetica", "normal");
    doc.line(rightLineX, fieldY2 + 1, rightLineX + rightLineW, fieldY2 + 1);

    // ── Particulars table ──
    const minRows = 6;
    const tableBody = (
      orders.length
        ? orders
        : [{ purchase_order_id: "", supplier_name: "", grand_total: total }]
    ).map((order: any, index: number) => {
      const amount = Number(order.grand_total || 0);
      const amountStr = formatNumbers(amount);
      const [pesos, cents] = amountStr.split(".");
      const particulars = [
        order.purchase_order_id || order.po_id
          ? `PO #${order.purchase_order_id || order.po_id}`
          : null,
        order.supplier_name || null,
        order.branch_name || null,
        order.purchase_request_id
          ? `PR #${order.purchase_request_id}`
          : null,
      ]
        .filter(Boolean)
        .join(" — ");

      return [
        String(index + 1),
        particulars || "Payment request",
        pesos,
        cents || "00",
      ];
    });

    while (tableBody.length < minRows) {
      tableBody.push(["", "", "", ""]);
    }

    // total row
    const totalStr = formatNumbers(total);
    const [totalPesos, totalCents] = totalStr.split(".");
    tableBody.push(["", "TOTAL", totalPesos, totalCents || "00"]);

    autoTable(doc, {
      startY: 62,
      margin: { left: marginX, right: marginX },
      head: [
        [
          { content: "NO.", styles: { halign: "center" } },
          { content: "PARTICULARS", styles: { halign: "center" } },
          { content: "AMOUNT", colSpan: 2, styles: { halign: "center" } },
        ],
      ],
      body: tableBody,
      theme: "grid",
      styles: {
        fontSize: 9,
        cellPadding: 2.5,
        lineColor: [0, 0, 0],
        lineWidth: 0.3,
        textColor: [0, 0, 0],
        minCellHeight: 8,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        valign: "middle",
      },
      columnStyles: {
        0: { cellWidth: 14, halign: "center" },
        1: { cellWidth: contentW - 14 - 28 - 16 },
        2: { cellWidth: 28, halign: "right" },
        3: { cellWidth: 16, halign: "left" },
      },
      didParseCell: (data) => {
        if (
          data.section === "body" &&
          data.row.index === tableBody.length - 1
        ) {
          data.cell.styles.fontStyle = "bold";
          if (data.column.index === 1) {
            data.cell.styles.halign = "right";
          }
        }
      },
    });

    const lastY = (doc as any).lastAutoTable?.finalY || 140;
    const sigY = Math.max(lastY + 18, 210);

    // ── Signature section ──
    const col1 = marginX;
    const col2 = marginX + contentW / 3;
    const col3 = marginX + (contentW * 2) / 3;
    const lineW = contentW / 3 - 10;

    const drawSignatureBlock = (
      x: number,
      y: number,
      label: string,
      name: string,
      subLabel: string
    ) => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.text(label, x, y);
      doc.line(x, y + 10, x + lineW, y + 10);
      if (name) {
        doc.setFontSize(8);
        doc.text(name, x + lineW / 2, y + 8, { align: "center" });
      }
      doc.setFontSize(8);
      doc.text(subLabel, x + lineW / 2, y + 14, { align: "center" });
    };

    drawSignatureBlock(col1, sigY, "prepared by:", preparedBy, "Name and Signature");
    drawSignatureBlock(col2, sigY, "Checked by:", checkedBy, "Name and Signature");
    drawSignatureBlock(
      col3,
      sigY,
      "Approved by:",
      approvedBy,
      "President/V-President"
    );

    drawSignatureBlock(
      col1,
      sigY + 28,
      "Received by:",
      "",
      "Name and Signature"
    );

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("Date", col2, sigY + 28);
    doc.line(col2, sigY + 38, col2 + lineW, sigY + 38);

    return doc;
  }

  async function generateCheckVoucher() {
    try {
      const doc = await buildCheckVoucherPdf();
      doc.save(`check-voucher-PR-${selectedRow.id}.pdf`);
      setVoucherGenerated(true);
      message.success("Check voucher generated.");
    } catch {
      message.error("Failed to generate check voucher.");
    }
  }

  async function printCheckVoucher() {
    try {
      const doc = await buildCheckVoucherPdf();
      const blobUrl = doc.output("bloburl");
      const printWindow = window.open(blobUrl);
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.focus();
          printWindow.print();
        };
      }
      setVoucherGenerated(true);
      message.success("Check voucher ready to print.");
    } catch {
      message.error("Failed to print check voucher.");
    }
  }

  const handleBeforeUpload = (file: any) => {
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) message.error(`${file.name} exceeds the 2MB size limit.`);
    return false;
  };

  const handleDepositUploadChange = (info: any) => {
    const validFiles = info.fileList
      .filter((f: any) => f.size / 1024 / 1024 < 2)
      .slice();
    depositForm.setFieldValue("deposit_attachments", validFiles);
  };

  async function submitDepositAttachments() {
    try {
      await depositForm.validateFields();
    } catch {
      return;
    }

    const files = depositForm.getFieldValue("deposit_attachments") || [];
    if (!files.length) {
      message.warning("Please upload at least one deposit slip or invoice.");
      return;
    }

    const formData = new FormData();
    formData.append("payment_request_id", String(selectedRow.id));
    files.forEach((f: any) => {
      if (f.originFileObj) {
        formData.append("attachment[]", f.originFileObj);
      }
    });

    setLoading("upload");
    const response = await postRequest(
      "payment_requests/upload_deposit_slip",
      formData,
      true
    );

    if (response.success) {
      message.success(
        formatString(
          response.data.message || "Attachments uploaded successfully."
        )
      );
      setTimeout(() => {
        setLoading("");
        closeActionModal();
        clearFields();
        fetchTableData();
      }, 800);
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          "Failed to upload deposit slip / invoice attachments."
      );
    }
  }

  function getTableColumns() {
    switch (activeTab) {
      case "pending":
        return [
          {
            title: "Payment Request #",
            dataIndex: "id",
            key: "id",
          },
          {
            title: "Source",
            dataIndex: "source_type",
            key: "source_type",
            render: (value: any) => formatSourceType(value),
          },
          {
            title: "Amount",
            dataIndex: "grand_total",
            key: "grand_total",
            align: "right" as any,
            render: (value: any) => formatNumbers(value),
          },
          {
            title: "Payment Method",
            dataIndex: "payment_method",
            key: "payment_method",
            render: (value: any) => formatString(value) || "-",
          },
          {
            title: "Created By",
            dataIndex: "added_by_name",
            key: "added_by_name",
          },
          {
            title: "",
            key: "action",
            width: 10,
            render: (_: any, record: any) => {
              const menu = (
                <Menu>
                  <Menu.Item
                    key="edit"
                    icon={<Pen strokeWidth={1.5} size={15} />}
                    onClick={() =>
                      navigateTo(`/payment-request/edit/${record.id}`)
                    }
                  >
                    Edit
                  </Menu.Item>
                  {hasRole(getUserRole(), "accounting", "accounting_ap", "admin") && (
                    <Menu.Item
                      key="check"
                      icon={<Eye strokeWidth={1.5} size={15} />}
                      onClick={() =>
                        navigateTo(`/payment-request/review/${record.id}`)
                      }
                    >
                      Check
                    </Menu.Item>
                  )}
                </Menu>
              );
              return (
                <Dropdown overlay={menu} trigger={["click"]}>
                  <Button>
                    Actions <ChevronDown strokeWidth={0.5} />
                  </Button>
                </Dropdown>
              );
            },
          },
        ];
      case "checked":
        return [
          {
            title: "Payment Request #",
            dataIndex: "id",
            key: "id",
          },
          {
            title: "Source",
            dataIndex: "source_type",
            key: "source_type",
            render: (value: any) => formatSourceType(value),
          },
          {
            title: "Amount",
            dataIndex: "grand_total",
            key: "grand_total",
            align: "right" as any,
            render: (value: any) => formatNumbers(value),
          },
          {
            title: "Payment Method",
            dataIndex: "payment_method",
            key: "payment_method",
            render: (value: any) => formatString(value) || "-",
          },
          {
            title: "Checked By",
            dataIndex: "checked_by_name",
            key: "checked_by_name",
          },
          {
            title: "Date Checked",
            dataIndex: "checked_on",
            key: "checked_on",
            render: (value: any) => formatDate(value, "MM/DD/YYYY"),
          },
          {
            title: "",
            key: "action",
            width: 10,
            render: (_: any, record: any) => {
              const menu = (
                <Menu>
                  <Menu.Item
                    key="view"
                    icon={<Eye strokeWidth={1.5} size={15} />}
                    onClick={() =>
                      navigateTo(`/payment-request/view/${record.id}`)
                    }
                  >
                    View
                  </Menu.Item>
                  {hasRole(getUserRole(), "supervisor", "admin") && (
                    <Menu.Item
                      key="approve"
                      icon={<Pen strokeWidth={1.5} size={15} />}
                      onClick={() =>
                        navigateTo(`/payment-request/approval/${record.id}`)
                      }
                    >
                      Approve
                    </Menu.Item>
                  )}
                </Menu>
              );
              return (
                <Dropdown overlay={menu} trigger={["click"]}>
                  <Button>
                    Actions <ChevronDown strokeWidth={0.5} />
                  </Button>
                </Dropdown>
              );
            },
          },
        ];
      case "approved":
        return [
          {
            title: "Payment Request #",
            dataIndex: "id",
            key: "id",
          },
          {
            title: "Source",
            dataIndex: "source_type",
            key: "source_type",
            render: (value: any) => formatSourceType(value),
          },
          {
            title: "Amount",
            dataIndex: "grand_total",
            key: "grand_total",
            align: "right" as any,
            render: (value: any) => formatNumbers(value),
          },
          {
            title: "Payment Method",
            dataIndex: "payment_method",
            key: "payment_method",
            render: (value: any) => formatString(value) || "-",
          },
          {
            title: "Approved By",
            dataIndex: "approved_by_name",
            key: "approved_by_name",
          },
          {
            title: "Date Approved",
            dataIndex: "approved_on",
            key: "approved_on",
            render: (value: any) => formatDate(value, "MM/DD/YYYY"),
          },
          {
            title: "Deposit Slip / Invoice",
            dataIndex: "deposit_slip",
            key: "deposit_slip",
            render: (_: any, record: any) => {
              const urls = getDepositSlipUrls(record);
              if (!urls.length) return "-";

              if (urls.length > 1) {
                const menu = (
                  <Menu>
                    {urls.map((url: string, index: number) => (
                      <Menu.Item
                        key={index}
                        onClick={(e) => {
                          e.domEvent.stopPropagation();
                          setShowAttachment(url);
                        }}
                      >
                        Attachment {index + 1}
                      </Menu.Item>
                    ))}
                  </Menu>
                );
                return (
                  <Dropdown overlay={menu} trigger={["click"]}>
                    <Button
                      size="small"
                      icon={<Eye size={14} />}
                      onClick={(e) => e.stopPropagation()}
                    >
                      View ({urls.length})
                    </Button>
                  </Dropdown>
                );
              }

              return (
                <Button
                  size="small"
                  icon={<Eye size={14} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAttachment(urls[0]);
                  }}
                >
                  View
                </Button>
              );
            },
          },
          {
            title: "",
            key: "action",
            width: 10,
            render: (_: any, record: any) => {
              const isCash =
                String(record.payment_method || "").toLowerCase() === "cash";
              const menu = (
                <Menu>
                  <Menu.Item
                    key="view"
                    icon={<Eye strokeWidth={1.5} size={15} />}
                    onClick={() =>
                      navigateTo(`/payment-request/view/${record.id}`)
                    }
                  >
                    View
                  </Menu.Item>
                  {hasRole(getUserRole(), "accounting", "accounting_ap", "admin") && (
                    <Menu.Item
                      key="voucher"
                      icon={<FileText strokeWidth={1.5} size={15} />}
                      disabled={isCash}
                      onClick={() => openCheckVoucherModal(record)}
                    >
                      Generate Check Voucher
                    </Menu.Item>
                  )}
                  {hasRole(getUserRole(), "custodian", "accounting", "accounting_ap", "admin", "treasury_in_charge", "treasury_assistant") && (
                    <Menu.Item
                      key="upload"
                      icon={<UploadIcon strokeWidth={1.5} size={15} />}
                      disabled={isCash}
                      onClick={() => openDepositSlipModal(record)}
                    >
                      Upload Signed CV / Deposit Slip
                    </Menu.Item>
                  )}
                </Menu>
              );
              return (
                <Dropdown overlay={menu} trigger={["click"]}>
                  <Button>
                    Actions <ChevronDown strokeWidth={0.5} />
                  </Button>
                </Dropdown>
              );
            },
          },
        ];
      case "disapproved":
        return [
          {
            title: "Payment Request #",
            dataIndex: "id",
            key: "id",
          },
          {
            title: "Source",
            dataIndex: "source_type",
            key: "source_type",
            render: (value: any) => formatSourceType(value),
          },
          {
            title: "Amount",
            dataIndex: "grand_total",
            key: "grand_total",
            align: "right" as any,
            render: (value: any) => formatNumbers(value),
          },
          {
            title: "Payment Method",
            dataIndex: "payment_method",
            key: "payment_method",
            render: (value: any) => formatString(value) || "-",
          },
          {
            title: "Disapproved By",
            dataIndex: "disapproved_by_name",
            key: "disapproved_by_name",
          },
          {
            title: "Date Disapproved",
            dataIndex: "disapproved_on",
            key: "disapproved_on",
            render: (value: any) => formatDate(value, "MM/DD/YYYY"),
          },
          {
            title: "Reason",
            dataIndex: "reason",
            key: "reason",
            render: (value: any, record: any) =>
              value || record.disapprove_reason || record.remarks || "-",
          },
        ];
      default:
        return [];
    }
  }

  function handleFilterChange(value: any, field: string) {
    if (field === "dates") {
      if (!value || value.length !== 2) {
        setFilterConfig((prev: any) => ({
          ...prev,
          date_from: "",
          date_to: "",
          page: 1,
        }));
        return;
      }
      setFilterConfig((prev: any) => ({
        ...prev,
        date_from: value[0]?.startOf("month").format("YYYY-MM-DD"),
        date_to: value[1]?.endOf("month").format("YYYY-MM-DD"),
        page: 1,
      }));
      return;
    }

    setFilterConfig((prev: any) => ({
      ...prev,
      [field]: value ?? "",
      page: 1,
    }));
  }

  return (
    <Layout className="page">
      <Helmet>
        <title>Agua Lab ERP System</title>
      </Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title="Payment Requests"
              withDateRangeFilter={false}
              withAddButton={hasRole(getUserRole(), "admin", "accounting", "accounting_ap")}
              addBtnText="Payment Request"
              onCreate={() => navigateTo("/payment-request/add")}
              withSearchBtn={true}
              onSearch={(value: any) =>
                setFilterConfig((prev: any) => ({
                  ...prev,
                  search: value ?? "",
                  page: 1,
                }))
              }
            />
          </Col>
          <Col xs={24} className="mb-1">
            <span>
              <strong>Filter By:</strong>
            </span>
          </Col>
          <Col xs={24} md={12} lg={8} className="mb-3">
            <RangePicker
              picker="month"
              value={[
                filterConfig.date_from ? dayjs(filterConfig.date_from) : null,
                filterConfig.date_to ? dayjs(filterConfig.date_to) : null,
              ]}
              format="MMMM YYYY"
              onChange={(e) => handleFilterChange(e, "dates")}
              allowClear
              className="w-full"
            />
          </Col>
          <Col span={24}>
            <div className="table-container overflow-auto">
              <div className="min-w-[800px]">
                <Tabs
                  type="card"
                  className="custom-tabs"
                  activeKey={activeTab}
                  onTabClick={onChangeTab}
                >
                  <TabPane tab="For Checking" key="pending">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={tableLoading}
                      size="small"
                      pagination={{
                        current: filterConfig.page,
                        total: dataSize,
                        showSizeChanger: true,
                        onChange: (page: number) => handleChangePage(page),
                        onShowSizeChange: (_: number, size: number) =>
                          handlePageSizeChange(size),
                      }}
                      rowClassName={() => "cursor-pointer"}
                      onRow={(record: any) => ({
                        onClick: () => {
                          setSelectedRow(record);
                          form.setFieldsValue(record);
                        },
                      })}
                    />
                  </TabPane>
                  <TabPane tab="Checked" key="checked">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={tableLoading}
                      size="small"
                      pagination={{
                        current: filterConfig.page,
                        total: dataSize,
                        showSizeChanger: true,
                        onChange: (page: number) => handleChangePage(page),
                        onShowSizeChange: (_: number, size: number) =>
                          handlePageSizeChange(size),
                      }}
                      rowClassName={() => "cursor-pointer"}
                      onRow={(record: any) => ({
                        onClick: () => {
                          setSelectedRow(record);
                          form.setFieldsValue(record);
                        },
                      })}
                    />
                  </TabPane>
                  <TabPane tab="Approved" key="approved">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={tableLoading}
                      size="small"
                      pagination={{
                        current: filterConfig.page,
                        total: dataSize,
                        showSizeChanger: true,
                        onChange: (page: number) => handleChangePage(page),
                        onShowSizeChange: (_: number, size: number) =>
                          handlePageSizeChange(size),
                      }}
                      rowClassName={() => "cursor-pointer"}
                      onRow={(record: any) => ({
                        onClick: () => {
                          setSelectedRow(record);
                          form.setFieldsValue(record);
                        },
                      })}
                    />
                  </TabPane>
                  <TabPane tab="Disapproved" key="disapproved">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={tableLoading}
                      size="small"
                      pagination={{
                        current: filterConfig.page,
                        total: dataSize,
                        showSizeChanger: true,
                        onChange: (page: number) => handleChangePage(page),
                        onShowSizeChange: (_: number, size: number) =>
                          handlePageSizeChange(size),
                      }}
                      rowClassName={() => "cursor-pointer"}
                      onRow={(record: any) => ({
                        onClick: () => {
                          setSelectedRow(record);
                          form.setFieldsValue(record);
                        },
                      })}
                    />
                  </TabPane>
                </Tabs>
              </div>
            </div>
          </Col>
        </Row>
      </Layout>

      {/* Check Voucher Modal */}
      <Modal
        open={showModal === "voucher"}
        title={`Check Voucher Form — PR #${selectedRow?.id ?? ""}`}
        onCancel={closeActionModal}
        maskClosable={false}
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={closeActionModal}>Close</Button>
          </div>
        }
      >
        <p className="mb-4 text-textColor">
          Generate and print the check voucher for this approved payment
          request.
        </p>
        <div className="flex flex-wrap gap-3">
          <Button
            type="primary"
            icon={<FileText size={16} />}
            loading={loading === "voucher"}
            disabled={!voucherDetail && loading === "voucher"}
            onClick={generateCheckVoucher}
          >
            Generate Check Voucher
          </Button>
          <Button
            icon={<Printer size={16} />}
            loading={loading === "voucher"}
            disabled={!voucherDetail && loading === "voucher"}
            onClick={printCheckVoucher}
          >
            Print Check Voucher
          </Button>
        </div>
        {voucherGenerated && (
          <p className="mt-3 text-green-600">
            Check voucher ready. You can close this dialog or print again.
          </p>
        )}
      </Modal>

      {/* Deposit Slip & Invoice Modal */}
      <Modal
        open={showModal === "deposit"}
        title={`Upload Signed CV / Deposit Slip — PR #${selectedRow?.id ?? ""}`}
        onCancel={closeActionModal}
        maskClosable={false}
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={closeActionModal}>Close</Button>
            <Button
              type="primary"
              loading={loading === "upload"}
              onClick={submitDepositAttachments}
            >
              Submit Attachments
            </Button>
          </div>
        }
      >
        <Form form={depositForm} layout="vertical">
          <Form.Item
            label="Deposit Slip and Invoice (multiple attachments)"
            name="deposit_attachments"
            valuePropName="fileList"
            getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
            rules={[
              { required: true, message: "Please upload at least one file." },
            ]}
          >
            <Dragger
              name="file"
              multiple
              beforeUpload={handleBeforeUpload}
              onChange={handleDepositUploadChange}
              accept=".pdf,image/*"
            >
              <p className="ant-upload-drag-icon flex justify-center items-center text-3xl">
                <Inbox />
              </p>
              <p className="ant-upload-text">
                Click or drag signed CV form / deposit slip files
              </p>
              <p className="ant-upload-hint">
                Supports PDF and image formats. Multiple files allowed.
              </p>
            </Dragger>
          </Form.Item>
          <p className="text-textColor -mt-2">
            Note: Maximum file size is 2MB per file.
          </p>
        </Form>
      </Modal>

      <ViewAttachmentModal
        showModal={!!showAttachment}
        attachmentUrl={showAttachment ?? ""}
        onCancel={() => setShowAttachment(null)}
      />
      <PersistentFooter />
    </Layout>
  );
};

export default PaymentRequest;
